import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

interface IssueCardProps {
	title: string;
	subtitle?: string;
	issuesText?: string;
	issueCount: number;
	isOpen: boolean;
	onToggle?: () => void;
	disableWhenEmpty?: boolean;
	ariaLabel?: string;
	titleAttr?: string;
}

const baseClass = "rounded-t-lg border bg-white p-2 text-left w-full";

export default function IssueCard({
	title,
	subtitle,
	issuesText,
	issueCount,
	isOpen,
	onToggle,
	disableWhenEmpty = false,
	ariaLabel,
	titleAttr,
}: IssueCardProps) {
	const hasIssues = issueCount > 0;
	const isInteractive = Boolean(onToggle) && (!disableWhenEmpty || hasIssues);

	const className = [
		baseClass,
		isInteractive ? "cursor-pointer hover:bg-gray-50" : "cursor-default",
		isOpen ? "border-b-primary-600 bg-primary-50 border-b-3" : "",
	].join(" ");

	const issuesSubtitle =
		issuesText ??
		(hasIssues ? `${issueCount} issue${issueCount == 1 ? "" : "s"}` : "No issues");

	const issuesSubtitleClass = hasIssues ? "text-xs text-red-700" : "text-xs text-gray-500";

	const content = (
		<div className="flex justify-between">
			<div>
				<p className={`${isOpen ? "font-semibold" : ""} text-gray-600`}>{title}</p>
				<p className="text-sm font-semibold text-gray-900">{subtitle}</p>
			</div>

			<div className="flex flex-col items-center">
				{hasIssues ? (
					<ExclamationTriangleIcon className="size-7 text-red-500" />
				) : (
					<CheckCircleIcon className="size-7 text-green-500" />
				)}
				<p className={issuesSubtitleClass}>{issuesSubtitle}</p>
			</div>
		</div>
	);

	if (!isInteractive) {
		return <div className={className}>{content}</div>;
	}

	return (
		<button
			type="button"
			className={className}
			onClick={onToggle}
			aria-pressed={isOpen}
			aria-label={ariaLabel}
			title={titleAttr}>
			{content}
		</button>
	);
}
